import DomainCard from '@/components/Domains/DomainCard.tsx';
import DomainCardPlaceholder from '@/components/Domains/DomainCardPlaceholder.tsx';
import { SettingTitle } from '@/components/settings/SettingsTitle.tsx';
import { SnackbarContext } from '@/contexts/SnackbarContext.tsx';
import { UserContext } from '@/contexts/UserContext.tsx';
import { useHasFeature } from '@/hooks/useHasFeature.ts';
import { addDomain } from '@/services/DomainsService.ts';
import { emptyConverter } from '@/utils';
import { track } from '@/utils/analytics.ts';
import * as Sentry from '@sentry/react';
import { Domain } from '@superfunnel/types';
import Loader from '@superfunnel/ui-core/Loader';
import Button from '@superfunnel/ui-core/v2/Button';
import Input from '@superfunnel/ui-core/v2/Input';
import {
    collection,
    DocumentData,
    onSnapshot,
    QueryDocumentSnapshot,
} from 'firebase/firestore';
import { useCallback, useContext, useEffect, useState } from 'react';

export const DomainsTab = () => {
  const { selectedAccount } = useContext(UserContext);
  const { show } = useContext(SnackbarContext);

  const hasAddDomainFeature = useHasFeature('domains:add');

  const [domain, setDomain] = useState('');
  const [domainsList, setDomainsList] =
    useState<QueryDocumentSnapshot<Domain, DocumentData>[]>();
  const [disabled, setDisabled] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<{ domain: string; error: any } | null>(
    null,
  );
  useEffect(() => {
    if (domain.length == 0) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [domain]);

  useEffect(() => {
    if (adding) setDisabled(true);
  }, [adding]);

  useEffect(() => {
    if (!selectedAccount) {
      setDomainsList([] as any);
      return;
    }

    const domainsRef = collection(selectedAccount.ref, 'domains').withConverter(
      emptyConverter<Domain>(),
    );

    const unsubscribe = onSnapshot(domainsRef, (domains) => {
      setDomainsList(
        domains.docs.sort(
          (a, b) =>
            (b.data().createdAt?.seconds ?? 0) -
            (a.data().createdAt?.seconds ?? 0),
        ),
      );
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  const onAddDomainClick = useCallback(async () => {
    track('add_domain_button_click', {
      domain,
    });

    setAdding(true);
    try {
      await addDomain(selectedAccount?.id ?? '', domain);
      setDomain('');
    } catch (e: any) {
      Sentry.captureException(e);
      show(e.message);
      setError({ domain, error: e });
    } finally {
      setAdding(false);
    }
  }, [domain, selectedAccount?.id, show]);

  return (
    <div className={'mx-auto flex max-w-161 flex-col gap-5 px-2.5'}>
      <SettingTitle
        title="Domains"
        description="Connect your custom domain for a branded web address."
      />
      {hasAddDomainFeature && (
        <div className={'space-y-2.5'}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              onAddDomainClick();
            }}
            className="grid grid-cols-[1fr_auto] gap-2"
          >
            <Input
              className="rounded-2lg h-10"
              type="text"
              name="domain"
              onInput={(e: any) => {
                setDomain(e.target.value);
              }}
              autoComplete="off"
              placeholder="mydomain.com"
              pattern="^(?:[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.)?[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$"
              required
            />
            <Button
              className="rounded-2lg h-10"
              type="submit"
              disabled={disabled}
            >
              {adding ? <Loader /> : 'Add'}
            </Button>
          </form>

          {error && (
            <div className="text-error-500 flex w-full items-center space-x-2 text-left text-sm">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                shapeRendering="geometricPrecision"
                style={{ color: '#f44336' }}
              >
                <circle cx="12" cy="12" r="10" fill="white" />
                <path d="M12 8v4" stroke="#f44336" />
                <path d="M12 16h.01" stroke="#f44336" />
              </svg>
              <p>
                Cannot add <b>{error.domain}</b> since it&apos;s already
                assigned to another project.
              </p>
            </div>
          )}
        </div>
      )}
      <div className={'space-y-3'}>
        {domainsList
          ? domainsList.map((domain) => {
              return (
                <DomainCard
                  key={domain.id}
                  domain={domain.data()}
                  domainId={domain.id}
                />
              );
            })
          : [1, 2, 3, 4, 5].map((v) => {
              return <DomainCardPlaceholder key={v + '-index'} />;
            })}
      </div>
    </div>
  );
};
